import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

interface FullPageEmbedProps {
  htmlCode: string;
  reactCode: string;
  liveUrl: string;
}

const CodeBlock = ({ code, label }: { code: string; label: string }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Code copied to clipboard!");
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <div className="relative">
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
        <code>{code}</code>
      </pre>
      <Button size="sm" variant="secondary" className="absolute top-2 right-2" onClick={copyToClipboard}>
        {copied ? (
          <>
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Copied!
          </>
        ) : (
          <>
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            {label}
          </>
        )}
      </Button>
    </div>
  );
};

export const FullPageEmbed = ({ htmlCode, reactCode, liveUrl }: FullPageEmbedProps) => (
  <Card className="p-4">
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold mb-2">Full Page Embed</h4>
        <p className="text-sm text-gray-600">
          Embed the full agent interface directly inside your page — no floating widget, just the full experience inside
          your layout.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> Set the parent container to a fixed height (e.g.{" "}
          <code className="font-mono">height: 700px</code>) so the iframe fills it properly.
        </p>
      </div>

      <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3">
        <p className="text-sm font-semibold text-amber-800 mb-1">⚠ Important — before you embed</p>
        <p className="text-sm text-amber-700">
          Some websites — especially those served via Google Workspace or similar platforms — restrict iFrame embedding.
          HelpGenie requires microphone access to function. Please confirm your website and hosting provider support both
          iFrame embedding and microphone permissions before going live.
        </p>
      </div>

      <Tabs defaultValue="html" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-3">
          <TabsTrigger value="html">HTML</TabsTrigger>
          <TabsTrigger value="react">React</TabsTrigger>
        </TabsList>

        <TabsContent value="html">
          <CodeBlock code={htmlCode} label="Copy HTML" />
        </TabsContent>

        <TabsContent value="react">
          <CodeBlock code={reactCode} label="Copy JSX" />
        </TabsContent>
      </Tabs>

      <div className="flex items-center gap-2 pt-1">
        <span className="text-xs text-gray-500">Live URL:</span>
        <a
          href={liveUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary underline underline-offset-2 hover:opacity-80 truncate"
        >
          {liveUrl}
        </a>
      </div>
    </div>
  </Card>
);
